import { Link } from '@mui/material';
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AssetLink } from '../components/AssetLink';
import type { PlayoutReport__TopAsset } from '../queries/PlayoutReport.generated';
import { formatMinutes, zoneDisplayLabel } from './helpers';

export type TopAssetRow = PlayoutReport__TopAsset;

// The events/plays/duration metric columns (and their per-event averages)
// shared by the Top Assets, By Sponsor, By Asset, and associated-sponsors
// tables. averages: false drops the two per-event average columns for the
// compact tables that don't show them.
export const useEventMetricColumns = <R extends GridValidRowModel>({
  averages = true,
}: { averages?: boolean } = {}): GridColDef<R>[] => {
  const { t } = useTranslation();

  return useMemo<GridColDef<R>[]>(() => {
    const columns: GridColDef<R>[] = [
      {
        field: 'eventCount',
        headerName: t('pages.playout_reports.top_assets.columns.events'),
        width: 80,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'plays',
        headerName: t('pages.playout_reports.top_assets.columns.plays'),
        width: 100,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => Number(value).toLocaleString(),
      },
      {
        field: 'durationMs',
        headerName: t('pages.playout_reports.top_assets.columns.duration'),
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => formatMinutes(value).toLocaleString(),
        renderCell: ({ formattedValue }) => <strong>{formattedValue}</strong>,
      },
    ];
    if (averages) {
      columns.push(
        {
          field: 'avgPlaysPerEvent',
          headerName: t('pages.playout_reports.top_assets.columns.avg_plays'),
          width: 140,
          align: 'right',
          headerAlign: 'right',
          valueFormatter: (value: number) =>
            value.toLocaleString(undefined, { maximumFractionDigits: 1 }),
        },
        {
          field: 'avgDurationMsPerEvent',
          headerName: t('pages.playout_reports.top_assets.columns.avg_exposure'),
          width: 160,
          align: 'right',
          headerAlign: 'right',
          valueFormatter: (value: number) => `${formatMinutes(value).toLocaleString()} m`,
        },
      );
    }
    return columns;
  }, [t, averages]);
};

// The three device columns shared across the Top Assets, By Sponsor, and By
// Asset tables — plain field columns, since every row now carries its device
// metrics inline.
export const useDeviceMetricColumns = <R extends GridValidRowModel>(): GridColDef<R>[] => {
  const { t } = useTranslation();

  return useMemo<GridColDef<R>[]>(
    () => [
      {
        field: 'deviceCount',
        headerName: t('pages.playout_reports.top_assets.columns.devices'),
        width: 90,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => value.toLocaleString(),
      },
      {
        field: 'avgPlaysPerEventPerDevice',
        headerName: t('pages.playout_reports.top_assets.columns.avg_plays_per_event_per_device'),
        width: 160,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) =>
          value.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      },
      {
        field: 'avgDurationMsPerEventPerDevice',
        headerName: t('pages.playout_reports.top_assets.columns.avg_exposure_per_event_per_device'),
        width: 180,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => `${formatMinutes(value).toLocaleString()} m`,
      },
    ],
    [t],
  );
};

export const useColumns = (): GridColDef<TopAssetRow>[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const eventMetricColumns = useEventMetricColumns<TopAssetRow>();
  const deviceColumns = useDeviceMetricColumns<TopAssetRow>();

  return useMemo<GridColDef<TopAssetRow>[]>(
    () => [
      {
        field: 'sponsor',
        headerName: t('pages.playout_reports.top_assets.columns.sponsor'),
        flex: 1,
        renderCell: ({ row }) =>
          row.sponsorId != null && row.sponsor ? (
            <Link
              component="button"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                navigate({ pathname: `by-sponsor/${row.sponsorId}`, search });
              }}
            >
              {row.sponsor}
            </Link>
          ) : (
            row.sponsor
          ),
      },
      {
        field: 'asset',
        headerName: t('pages.playout_reports.top_assets.columns.asset'),
        flex: 1.5,
        hideable: false,
        renderCell: ({ row }) => (
          <AssetLink
            kind={row.kind}
            mediaItemId={row.mediaItemId ?? null}
            contentItemId={row.contentItemId ?? null}
            contentFolderId={row.contentFolderId ?? null}
            label={row.asset}
          />
        ),
      },
      {
        field: 'zones',
        headerName: t('pages.playout_reports.top_assets.columns.asset_type'),
        flex: 1,
        valueGetter: (_value, row) => row.zones.map((z) => zoneDisplayLabel(z)).join(', '),
      },
      ...eventMetricColumns,
      ...deviceColumns,
    ],
    [t, navigate, search, eventMetricColumns, deviceColumns],
  );
};
